import { useRef, useState, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Environment } from '@react-three/drei';
import { Spherical, Vector3, MathUtils } from 'three';
import './Scene.css';

function FixedLight({ intensity = 1 }) {
  const { camera } = useThree();
  const lightRef = useRef();

  useFrame(() => {
    if (lightRef.current) {
      lightRef.current.position.copy(camera.position);
    }
  });

  return <directionalLight ref={lightRef} intensity={intensity} />;
}

// This component manages the camera controls and the reset logic.
// It MUST be a child of the Canvas component to use R3F hooks like useFrame.
const CameraManager = ({ isObjectSelected = false }) => {
  const controlsRef = useRef();
  const { camera } = useThree();
  const [isResetting, setIsResetting] = useState(false);

  // Disable/enable controls based on object selection
  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.enableRotate = !isObjectSelected;
      controlsRef.current.enableZoom = !isObjectSelected;
      controlsRef.current.enablePan = !isObjectSelected;
      controlsRef.current.autoRotate = !isObjectSelected;
    }
  }, [isObjectSelected]);

  // This frame loop animates the camera's vertical angle (phi) back to horizontal.
  useFrame(({ camera }) => {

    if (isResetting && controlsRef.current) {
      const controls = controlsRef.current;
      const center = controls.target;

      const spherical = new Spherical().setFromVector3(camera.position.clone().sub(center));
      const targetPhi = Math.PI / 2;

      spherical.phi = MathUtils.lerp(spherical.phi, targetPhi, 0.05);

      const newPosition = new Vector3().setFromSpherical(spherical).add(center);
      camera.position.copy(newPosition);

      controls.update();

      if (Math.abs(spherical.phi - targetPhi) < 0.001) {
        spherical.phi = targetPhi;
        const finalPosition = new Vector3().setFromSpherical(spherical).add(center);
        camera.position.copy(finalPosition);
        setIsResetting(false);
      }
    }
  });

  // Store initial camera position and target
  const initialCameraState = useRef({
    position: new Vector3(0, 0.3, 1.4),
    target: new Vector3(0, 0, 0)
  });

  // Animation state
  const animationRef = useRef({
    isAnimating: false,
    startPosition: new Vector3(),
    startTarget: new Vector3(),
    progress: 0,
    duration: 0.8 // Animation duration in seconds
  });

  // This effect listens for the reset event
  useEffect(() => {
    const handleReset = () => {
      if (controlsRef.current && !animationRef.current.isAnimating) {
        // Start animation
        animationRef.current = {
          isAnimating: true,
          startPosition: camera.position.clone(),
          startTarget: controlsRef.current.target.clone(),
          progress: 0,
          duration: 0.8
        };

        // Also reset tilt
        setIsResetting(true);
      }
    };

    window.addEventListener('resetCameraTilt', handleReset);
    return () => {
      window.removeEventListener('resetCameraTilt', handleReset);
    };
  }, [camera]);

  // Animation frame loop
  useFrame((_, delta) => {
    if (animationRef.current.isAnimating) {
      const anim = animationRef.current;
      anim.progress = Math.min(1, anim.progress + (delta / anim.duration));

      // Smooth step for easing
      const t = anim.progress < 0.5
        ? 2 * anim.progress * anim.progress
        : 1 - Math.pow(-2 * anim.progress + 2, 2) / 2;

      // Interpolate position and target
      camera.position.lerpVectors(
        anim.startPosition,
        initialCameraState.current.position,
        t
      );

      const newTarget = new Vector3().lerpVectors(
        anim.startTarget,
        initialCameraState.current.target,
        t
      );

      controlsRef.current.target.copy(newTarget);
      controlsRef.current.update();

      // End of animation
      if (anim.progress >= 1) {
        anim.isAnimating = false;
      }
    }
  });

  return (
    <>
      <Environment preset="studio" environmentIntensity={.5} environmentRotation={[0, 0, 0]} />
      <OrbitControls
        ref={controlsRef}
        enableDamping
        dampingFactor={0.05}
        rotateSpeed={0.5}
        zoomSpeed={0.8}
        autoRotate={true}
        autoRotateSpeed={0.3}
        enableZoom={!isObjectSelected}
        enablePan={false}
        minPolarAngle={Math.PI / 8}
        maxPolarAngle={Math.PI / 1.5}
        enabled={!isObjectSelected}
        minDistance={1}       // Mindestabstand (Zoom-in Grenze)
        maxDistance={2.4}
      />
      <FixedLight position={[-10, 0, -10]} intensity={0.4} />
      <FixedLight position={[10, 0, -10]} intensity={0.4} />
      <FixedLight position={[-10, 0, 10]} intensity={0.4} />
      <FixedLight position={[10, 0, 10]} intensity={0.4} />
      <FixedLight position={[-10, 10, 10]} intensity={0.4} />
      <FixedLight position={[10, -10, 10]} intensity={0.4} />

      <FixedLight position={[-5, 0, 5]} intensity={0.4} />
      <FixedLight position={[5, 0, 5]} intensity={0.4} />
      <FixedLight position={[-5, 0, 5]} intensity={0.4} />
      <FixedLight position={[5, 0, 5]} intensity={0.4} />
      <FixedLight position={[-5, 5, 5]} intensity={0.4} />
      <FixedLight position={[5, -5, 5]} intensity={0.4} />
    </>
  );
};

const Scene = ({ children, isObjectSelected = false }) => {
  return (
    <div className="canvas-container">
      <Canvas camera={{ position: [0, 0.3, 1.4], fov: 55, }}>
        <CameraManager isObjectSelected={isObjectSelected} />
        {children}
      </Canvas>
    </div>
  );
};

export default Scene;
